import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Plus, Search, MoreVertical, Shield, Eye, XCircle,
  Building2, CheckCircle, Loader2, Filter,
} from "lucide-react";
import { toast } from "sonner";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import api, { userAPI, tenantAPI, authAPI } from "../../lib/api";

const ROLE_COLORS = {
  super_admin: "bg-red-50 text-red-700 border-red-200",
  tenant_admin: "bg-blue-50 text-blue-700 border-blue-200",
  manager: "bg-purple-50 text-purple-700 border-purple-200",
  learner: "bg-green-50 text-green-700 border-green-200",
  trainer: "bg-amber-50 text-amber-700 border-amber-200",
};

const AllUsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterTenant, setFilterTenant] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditRoleOpen, setIsEditRoleOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [formData, setFormData] = useState({
    email: "", password: "", full_name: "", role: "learner", tenant_id: "",
  });

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [usersRes, tenantsRes] = await Promise.all([
        userAPI.list(),
        tenantAPI.list(),
      ]);
      // Filter out super_admin users — this page is for tenant users only
      const tenantUsers = (usersRes.data || []).filter(u => u.role !== "super_admin" && u.tenant_id);
      setUsers(tenantUsers);
      setTenants(tenantsRes.data || []);
    } catch (e) {
      console.error("Failed to fetch:", e);
      toast.error("Failed to load users");
    } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    try {
      await authAPI.register(formData);
      toast.success("User created");
      setIsCreateOpen(false);
      setFormData({ email: "", password: "", full_name: "", role: "learner", tenant_id: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create user");
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;
    try {
      await userAPI.updateRole(selectedUser.id, newRole);
      toast.success("Role updated");
      setIsEditRoleOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update role");
    }
  };

  const handleDeactivate = async (user) => {
    if (!window.confirm(`Deactivate ${user.full_name}?`)) return;
    try {
      await api.put(`/users/${user.id}/status`, { status: "inactive" });
      toast.success("User deactivated");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filteredUsers = users.filter((u) => {
    const matchSearch = !searchQuery || u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) || u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchRole = !filterRole || u.role === filterRole;
    const matchTenant = !filterTenant || u.tenant_id === filterTenant;
    return matchSearch && matchRole && matchTenant;
  });

  const getInitials = (name) => (name || "?").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  const getTenantName = (tid) => tenants.find(t => t.id === tid)?.name || "—";

  return (
    <AdminLayout title="Users & Teams" variant="super_admin">
      <div className="space-y-6" data-testid="all-users-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>
              Users & Teams
            </h2>
            <p className="text-sm text-muted-foreground">All tenant users across businesses — Business Admins, Managers, Trainers, Learners</p>
          </div>
          <Button className="btn-primary" onClick={() => setIsCreateOpen(true)} data-testid="add-user-btn">
            <Plus className="w-4 h-4 mr-2" /> Add User
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {["tenant_admin", "manager", "trainer", "learner"].map(role => (
            <Card key={role}><CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{users.filter(u => u.role === role).length}</p>
              <p className="text-sm text-muted-foreground capitalize">{role === "tenant_admin" ? "Business Admins" : role + "s"}</p>
            </CardContent></Card>
          ))}
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold" data-testid="total-users">{users.length}</p>
            <p className="text-sm text-muted-foreground">Total</p>
          </CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search users..." className="pl-10" data-testid="user-search" />
          </div>
          <Select value={filterRole || "all"} onValueChange={(v) => setFilterRole(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[180px]" data-testid="role-filter"><SelectValue placeholder="All Roles" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="tenant_admin">Business Admin</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="trainer">Trainer</SelectItem>
              <SelectItem value="learner">Learner</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterTenant || "all"} onValueChange={(v) => setFilterTenant(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[200px]" data-testid="tenant-filter"><SelectValue placeholder="All Tenants" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tenants</SelectItem>
              {tenants.filter(t => t.status === "active").map(t => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Users Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No users found</h3>
                <p className="text-muted-foreground">{searchQuery ? "Try different filters" : "Add your first user"}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Tenant</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((u) => (
                      <TableRow key={u.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/admin/team/${u.id}`)} data-testid={`user-row-${u.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarFallback className="bg-primary/10 text-primary text-sm">{getInitials(u.full_name)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{u.full_name}</p>
                              <p className="text-sm text-muted-foreground">{u.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={ROLE_COLORS[u.role] || ""}>
                            {u.role === "tenant_admin" ? "Business Admin" : (u.role || "").replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{getTenantName(u.tenant_id)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {u.is_active !== false && u.status !== "inactive" ? (
                            <Badge className="bg-green-50 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>
                          ) : (
                            <Badge className="bg-red-50 text-red-700"><XCircle className="w-3 h-3 mr-1" />Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`user-actions-${u.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/admin/team/${u.id}`)}>
                                <Eye className="w-4 h-4 mr-2" /> View Profile
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setSelectedUser(u); setNewRole(u.role); setIsEditRoleOpen(true); }}>
                                <Shield className="w-4 h-4 mr-2" /> Change Role
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive" onClick={() => handleDeactivate(u)}>
                                <XCircle className="w-4 h-4 mr-2" /> Deactivate
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create User Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Tenant User</DialogTitle>
            <DialogDescription>Create a new user and assign them to a tenant</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} placeholder="John Doe" data-testid="new-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john@company.com" data-testid="new-user-email" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Password" data-testid="new-user-password" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tenant_admin">Business Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="trainer">Trainer</SelectItem>
                    <SelectItem value="learner">Learner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tenant</Label>
                <Select value={formData.tenant_id} onValueChange={(v) => setFormData({ ...formData, tenant_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select tenant" /></SelectTrigger>
                  <SelectContent>
                    {tenants.filter(t => t.status === "active").map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={handleCreate} data-testid="create-user-submit">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={isEditRoleOpen} onOpenChange={setIsEditRoleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Role — {selectedUser?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tenant_admin">Business Admin</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="trainer">Trainer</SelectItem>
                <SelectItem value="learner">Learner</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditRoleOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={handleUpdateRole}>Update Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AllUsersPage;
